import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { Link, useSearchParams } from 'react-router-dom'
import { Dialog, Tooltip } from '@mui/material'
import { toast } from 'react-toastify'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import Checkbox from 'components/atoms/Checkbox'
import { SkeletonRows } from 'components/Skeleton'
import { hideEmail } from 'utils/user'
import documentAccessAPI, { announceChange } from 'features/documentAccess/documentAccessAPI'
import { APPROVE_EXPLAINER, APPROVER_EXPLAINER, DECIDE_REASON_MESSAGE, SELF_GRANT_HELP, STATUS_META, WHO_DECIDES, errorMessage, formatDate, statusMeta } from 'features/documentAccess/accessCopy'
import { RAG } from 'features/dashboard/charts/scoreColour'
import { ACCENT, ACCENT_SOFT, BORDER, INK, LINK, MUTED, PrimaryButton, SecondaryButton, TextButton } from 'features/actions/workspace/ui'

const PAGE_SIZE = 25

const COLUMNS = [
  { key: 'document', label: 'Document' },
  { key: 'building', label: 'Building Passport' },
  { key: 'person', label: 'Person' },
  { key: 'asked', label: 'Asked' },
  { key: 'status', label: 'Status' },
]

const StatusPill = ({ status }) => {
  const meta = statusMeta(status)
  return (
    <Tooltip title={meta.help} placement='top'>
      <Pill tabIndex={0} style={{ color: meta.rag.fg, background: meta.rag.bg }}>{meta.short}</Pill>
    </Tooltip>
  )
}

const decideReasonMessage = x => (x.reason === 'already_decided'
  ? `Already decided by ${x.decidedBy || 'someone else'}`
  : DECIDE_REASON_MESSAGE[x.reason] || 'This request was not changed.')

const personName = p => (p?.name ? p.name : p?.email ? hideEmail(p.email) : 'Unknown')

const DocumentAccessRequestsPage = () => {
  const [params, setParams] = useSearchParams()
  const box = params.get('box') === 'mine' ? 'mine' : 'to_decide'
  const [status, setStatus] = useState(box === 'mine' ? 'all' : 'pending')
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [sort, setSort] = useState('-asked')
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(() => new Set())
  const [busy, setBusy] = useState(false)
  const [refuseIds, setRefuseIds] = useState(null)
  const [note, setNote] = useState('')
  const [summary, setSummary] = useState(null)
  const [rowNotes, setRowNotes] = useState({})
  const requestId = useRef(0)
  const keptRows = useRef({})

  useEffect(() => { setStatus(box === 'mine' ? 'all' : 'pending'); setSelected(new Set()); setQ(''); setDebouncedQ('') }, [box])

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300)
    return () => clearTimeout(t)
  }, [q])

  const fetchPage = useCallback(page => {
    const id = ++requestId.current
    setLoading(true)
    documentAccessAPI.list({ box, status, q: debouncedQ || undefined, sort, page, perPage: PAGE_SIZE })
      .then(res => {
        if (id !== requestId.current) return
        const incoming = res?.data?.data || []
        const m = res?.data?.meta || null
        setMeta(m)
        setRows(prev => ((m?.currentPage ?? 1) > 1
          ? [...prev, ...incoming.filter(i => !prev.some(x => x.id === i.id))]
          : [...incoming, ...Object.values(keptRows.current).filter(k => !incoming.some(i => i.id === k.id))]))
      })
      .catch(err => { if (id === requestId.current) toast.error(errorMessage(err, 'Could not load the requests.')) })
      .finally(() => { if (id === requestId.current) setLoading(false) })
  }, [box, status, debouncedQ, sort])

  useEffect(() => { setRows([]); setRowNotes({}); keptRows.current = {}; setSelected(new Set()); fetchPage(1) }, [fetchPage])

  const loadSummary = useCallback(() => {
    documentAccessAPI.summary().then(res => setSummary(res?.data?.data || null)).catch(() => setSummary(null))
  }, [])
  useEffect(() => { loadSummary() }, [loadSummary])

  const canAct = r => r.canDecide && !r.cannotDecideReason
  const decidable = useMemo(() => rows.filter(r => r.canDecide && !r.cannotDecideReason), [rows])
  const allSelected = decidable.length > 0 && decidable.every(r => selected.has(r.id))

  const toggle = id => setSelected(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  const reportSkipped = skipped => {
    if (!skipped.length) return
    const notes = {}
    skipped.forEach(x => {
      notes[x.id] = decideReasonMessage(x)
      const row = rows.find(r => r.id === x.id)
      if (row && ['already_decided', 'not_pending'].includes(x.reason)) keptRows.current[x.id] = { ...row, canDecide: false }
    })
    setRowNotes(prev => ({ ...prev, ...notes }))
    const needAttention = skipped.filter(x => x.reason !== 'already_decided')
    if (needAttention.length === 1) toast.error(decideReasonMessage(needAttention[0]))
    else if (needAttention.length > 1) toast.error(`${needAttention.length} requests were not changed. The reason is shown on each one.`)
  }

  const decide = async (ids, decision, decisionNote) => {
    setBusy(true)
    try {
      const res = await documentAccessAPI.decide(ids, decision, decisionNote)
      const results = res?.data?.data || []
      const n = results.filter(x => x.status === 'approved' || x.status === 'refused').length
      if (n > 0) {
        toast.success(decision === 'approve'
          ? `${n === 1 ? 'Request' : `${n} requests`} approved. ${n === 1 ? 'The document has' : 'Each document has'} been shared with the person who asked.`
          : `${n === 1 ? 'Request' : `${n} requests`} refused. We have let them know.`)
      }
      const decidedIds = results.filter(x => x.status === 'approved' || x.status === 'refused').map(x => x.id)
      decidedIds.forEach(id => { delete keptRows.current[id] })
      if (decidedIds.length) {
        setRowNotes(prev => {
          const next = { ...prev }
          decidedIds.forEach(id => { delete next[id] })
          return next
        })
      }
      reportSkipped(results.filter(x => x.status === 'skipped'))
      setSelected(new Set())
      announceChange()
      loadSummary()
      if (n > 0) fetchPage(1)
    } catch (err) {
      const results = err?.response?.data?.data
      if (Array.isArray(results) && results.length) {
        reportSkipped(results.map(x => ({ ...x, status: 'skipped' })))
      } else {
        toast.error(errorMessage(err))
      }
    } finally {
      setBusy(false)
    }
  }

  const selfGrant = async r => {
    setBusy(true)
    try {
      const res = await documentAccessAPI.selfGrant(r.id)
      if (res?.data?.canOpen === false) {
        toast.error(`You still cannot open ${r.file?.name || 'this document'}.`)
      } else {
        toast.success(`You can now open ${r.file?.name || 'the document'}.`)
      }
      announceChange()
      fetchPage(1)
    } catch (err) {
      toast.error(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const withdraw = async id => {
    setBusy(true)
    try {
      await documentAccessAPI.withdraw(id)
      toast.success('Request withdrawn.')
      announceChange()
      loadSummary()
      fetchPage(1)
    } catch (err) {
      toast.error(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const sortBy = key => setSort(prev => (prev === `-${key}` ? key : `-${key}`))
  const sortMark = key => (sort === key ? ' ▲' : sort === `-${key}` ? ' ▼' : '')
  const counts = meta?.counts || {}
  const totalAll = Object.values(counts).reduce((a, b) => a + b, 0)
  const hasMore = meta ? meta.currentPage < meta.lastPage : false

  const statusFilters = [
    { key: 'pending', label: 'Waiting', n: counts.pending || 0 },
    { key: 'approved', label: 'Approved', n: counts.approved || 0 },
    { key: 'refused', label: 'Refused', n: counts.refused || 0 },
    { key: 'withdrawn', label: 'Withdrawn', n: counts.withdrawn || 0 },
    { key: 'all', label: 'All', n: totalAll },
  ]

  const renderInfo = (r, ctx) => (
    <>
      {r.whyYouCanDecide && <Sub>{r.whyYouCanDecide}</Sub>}
      {r.cannotDecideReason && <Warn id={`reason-${ctx}-${r.id}`} role='note'>{r.cannotDecideReason}</Warn>}
      {rowNotes[r.id] && <Warn role='status'>{rowNotes[r.id]}</Warn>}
    </>
  )

  const renderActions = (r, ctx) => (
    <RowActions>
      {r.canDecide && (
        <>
          <PrimaryButton type='button' disabled={busy || !!r.cannotDecideReason} aria-describedby={r.cannotDecideReason ? `reason-${ctx}-${r.id}` : undefined} onClick={() => decide([r.id], 'approve')}>Approve</PrimaryButton>
          <SecondaryButton type='button' disabled={busy || !!r.cannotDecideReason} aria-describedby={r.cannotDecideReason ? `reason-${ctx}-${r.id}` : undefined} onClick={() => { setNote(''); setRefuseIds([r.id]) }}>Refuse</SecondaryButton>
        </>
      )}
      {r.canDecide && r.canSelfGrant && r.needsAccess && !r.cannotDecideReason && (
        <>
          <SecondaryButton type='button' disabled={busy} onClick={() => selfGrant(r)} data-tour='access-self-grant'>Give myself access</SecondaryButton>
          <Sub>{SELF_GRANT_HELP}</Sub>
        </>
      )}
      {r.canWithdraw && <TextButton type='button' disabled={busy} onClick={() => withdraw(r.id)}>Withdraw</TextButton>}
    </RowActions>
  )

  const shownPerson = r => (box === 'mine' ? r.uploader : r.requester)

  const personHeading = box === 'mine' ? 'Uploaded by' : 'Asked by'

  const decidedLine = r => {
    if (box !== 'mine') return null
    if ((r.status === 'approved' || r.status === 'refused') && r.decidedBy) return `Decided by ${personName(r.decidedBy)}`
    if (r.status === 'pending' && r.redirectedTo) return `Passed to ${personName(r.redirectedTo)} to decide`
    return null
  }

  return (
    <Page>
      <Head>
        <Title>Document access requests</Title>
        <Intro>
          When someone can see a document in a Building Passport but is not allowed to open it, they can ask for it here.
          {' '}{WHO_DECIDES}
          {' '}Each document is approved or refused on its own.
          {' '}{APPROVE_EXPLAINER}
          {' '}{APPROVER_EXPLAINER}
        </Intro>
      </Head>

      <Tabs role='tablist'>
        <Tab role='tab' aria-selected={box === 'to_decide'} $on={box === 'to_decide'} onClick={() => setParams({})}>
          Waiting for you{summary?.waitingForYou ? <TabCount>{summary.waitingForYou}</TabCount> : null}
        </Tab>
        <Tab role='tab' aria-selected={box === 'mine'} $on={box === 'mine'} onClick={() => setParams({ box: 'mine' })}>
          Your requests{summary?.yourPending ? <TabCount>{summary.yourPending}</TabCount> : null}
        </Tab>
      </Tabs>

      <Filters>
        <Search type='search' value={q} onChange={e => setQ(e.target.value)} placeholder='Search by document, building or person' aria-label='Search requests' />
        <Chips role='group' aria-label='Filter by status'>
          {statusFilters.map(f => {
            const rag = f.key === 'all' ? null : STATUS_META[f.key].rag
            return (
              <FilterChip key={f.key} type='button' aria-pressed={status === f.key} $on={status === f.key} $fg={rag?.fg} onClick={() => setStatus(f.key)}>
                {f.label} <span>({f.n})</span>
              </FilterChip>
            )
          })}
        </Chips>
        <MobileSort>
          <FilterChip type='button' $on={sort === '-asked'} onClick={() => setSort('-asked')}>Newest first</FilterChip>
          <FilterChip type='button' $on={sort === 'asked'} onClick={() => setSort('asked')}>Oldest first</FilterChip>
        </MobileSort>
      </Filters>

      {box === 'to_decide' && selected.size > 0 && (
        <BulkBar>
          <span>{selected.size} selected</span>
          <PrimaryButton type='button' disabled={busy} onClick={() => decide([...selected], 'approve')}>Approve selected</PrimaryButton>
          <SecondaryButton type='button' disabled={busy} onClick={() => { setNote(''); setRefuseIds([...selected]) }}>Refuse selected</SecondaryButton>
        </BulkBar>
      )}

      {meta && (
        <Showing>Showing {rows.length} of {meta.total}</Showing>
      )}

      {loading && !rows.length ? (
        <SkeletonRows rows={6} gap={12} widths={['100%']} />
      ) : !rows.length ? (
        <Empty>
          {box === 'to_decide'
            ? (status === 'pending' ? 'Nothing is waiting for you. Requests you can decide will appear here.' : 'No requests match these filters.')
            : 'You have not asked to open any documents yet. Locked documents in a Building Passport have a "Request access" button.'}
        </Empty>
      ) : (
        <>
          <Table role='table'>
            <thead>
              <tr>
                {box === 'to_decide' && (
                  <Th $narrow>
                    <Checkbox name='all' isChecked={allSelected} onChange={() => setSelected(allSelected ? new Set() : new Set(decidable.map(r => r.id)))} />
                  </Th>
                )}
                {COLUMNS.map(c => (
                  <Th key={c.key}>
                    <SortButton type='button' onClick={() => sortBy(c.key)} aria-label={`Sort by ${c.label}`}>
                      {c.key === 'person' ? personHeading : c.label}{sortMark(c.key)}
                    </SortButton>
                  </Th>
                ))}
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  {box === 'to_decide' && (
                    <Td $narrow>
                      {canAct(r) && <Checkbox name={r.id} isChecked={selected.has(r.id)} onChange={() => toggle(r.id)} />}
                    </Td>
                  )}
                  <Td>
                    <DocName>{r.file?.name || 'Document'}</DocName>
                    {r.message && <Sub>“{r.message}”</Sub>}
                    {renderInfo(r, 't')}
                  </Td>
                  <Td><BpLink to={`/bp-list/${r.buildingPassport?.id}`}>{r.buildingPassport?.name}</BpLink></Td>
                  <Td>{personName(shownPerson(r))}</Td>
                  <Td>{formatDate(r.askedAt)}</Td>
                  <Td>
                    <StatusPill status={r.status} />
                    {decidedLine(r) && <Sub>{decidedLine(r)}</Sub>}
                    {r.decisionNote && <Sub>Note: {r.decisionNote}</Sub>}
                  </Td>
                  <Td>
                    {renderActions(r, 't')}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Cards>
            {rows.map(r => (
              <Card key={r.id}>
                <CardTop>
                  {box === 'to_decide' && canAct(r) && (
                    <CardCheck><Checkbox name={`m-${r.id}`} isChecked={selected.has(r.id)} onChange={() => toggle(r.id)} /></CardCheck>
                  )}
                  <CardName>{r.file?.name || 'Document'}</CardName>
                </CardTop>
                <Sub>{r.buildingPassport?.name} · asked {formatDate(r.askedAt)}</Sub>
                <Sub>{personHeading}: {personName(shownPerson(r))}</Sub>
                {r.message && <Sub>“{r.message}”</Sub>}
                {renderInfo(r, 'c')}
                <StatusPill status={r.status} />
                {decidedLine(r) && <Sub>{decidedLine(r)}</Sub>}
                {renderActions(r, 'c')}
              </Card>
            ))}
          </Cards>

          <InfiniteScrollSentinel hasMore={hasMore} loading={loading} onLoadMore={() => fetchPage((meta?.currentPage || 1) + 1)} />
        </>
      )}

      <Dialog open={!!refuseIds} onClose={busy ? undefined : () => setRefuseIds(null)} fullWidth maxWidth='xs' aria-labelledby='refuse-title'
        PaperProps={{ sx: { borderRadius: '12px', m: 2, width: 'calc(100% - 32px)' } }}>
        <DialogBody>
          <DialogTitle id='refuse-title'>Refuse {refuseIds?.length > 1 ? `${refuseIds.length} requests` : 'this request'}?</DialogTitle>
          <Intro>The document stays locked for them. We will email them, and they can ask again after 7 days.</Intro>
          <label htmlFor='refuse-note'><strong>Add a note for them (optional)</strong></label>
          <Note id='refuse-note' value={note} maxLength={500} onChange={e => setNote(e.target.value)} placeholder='For example: please ask our facilities team instead.' />
          <DialogFooter>
            <SecondaryButton type='button' onClick={() => setRefuseIds(null)} disabled={busy}>Cancel</SecondaryButton>
            <PrimaryButton type='button' disabled={busy} onClick={async () => { const ids = refuseIds; setRefuseIds(null); await decide(ids, 'refuse', note.trim()) }}>Refuse</PrimaryButton>
          </DialogFooter>
        </DialogBody>
      </Dialog>
    </Page>
  )
}

export default DocumentAccessRequestsPage

const Page = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: 24px 16px 48px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Head = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Title = styled.h1`
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  color: ${INK};
`

const Intro = styled.p`
  margin: 0;
  max-width: 72ch;
  font-size: 15px;
  line-height: 1.55;
  color: ${MUTED};
`

const Tabs = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid ${BORDER};
`

const Tab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 14px;
  border: none;
  border-bottom: 3px solid ${({ $on }) => ($on ? ACCENT : 'transparent')};
  background: none;
  font: inherit;
  font-size: 15px;
  font-weight: ${({ $on }) => ($on ? 700 : 600)};
  color: ${({ $on }) => ($on ? INK : MUTED)};
  cursor: pointer;
`

const TabCount = styled.span`
  min-width: 22px;
  padding: 1px 7px;
  border-radius: 999px;
  background: ${ACCENT_SOFT};
  color: ${LINK};
  font-size: 13px;
  font-weight: 700;
`

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 12px;
`

const Search = styled.input`
  flex: 1 1 280px;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid #d4d4d8;
  border-radius: 8px;
  font-size: 16px;
  font-family: inherit;
  &:focus { outline: 2px solid ${ACCENT}; outline-offset: 1px; }
`

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const MobileSort = styled.div`
  display: none;
  gap: 6px;
  @media (max-width: 768px) { display: flex; }
`

const FilterChip = styled.button`
  min-height: 40px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid ${({ $on, $fg }) => ($on ? ($fg || ACCENT) : BORDER)};
  background: ${({ $on }) => ($on ? '#fff' : '#fafafa')};
  color: ${({ $on, $fg }) => ($on ? ($fg || LINK) : INK)};
  font: inherit;
  font-size: 14px;
  font-weight: ${({ $on }) => ($on ? 700 : 600)};
  cursor: pointer;
  span { font-weight: 600; color: ${MUTED}; }
`

const BulkBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 10px 12px;
  border: 1px solid ${BORDER};
  border-radius: 10px;
  background: ${ACCENT_SOFT};
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
`

const Showing = styled.div`
  font-size: 14px;
  color: ${MUTED};
`

const Empty = styled.p`
  margin: 0;
  padding: 24px 16px;
  border: 1px dashed ${BORDER};
  border-radius: 12px;
  font-size: 15px;
  line-height: 1.5;
  color: ${MUTED};
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  overflow: hidden;
  td, th { border-bottom: 1px solid #f1f1f3; }
  @media (max-width: 768px) { display: none; }
`

const Th = styled.th`
  padding: 10px 12px;
  width: ${({ $narrow }) => ($narrow ? '44px' : 'auto')};
  text-align: left;
  font-size: 13px;
  font-weight: 700;
  color: ${MUTED};
  background: #fafafa;
`

const SortButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-weight: 700;
  color: ${MUTED};
  cursor: pointer;
  &:hover { color: ${INK}; }
`

const Td = styled.td`
  padding: 12px;
  width: ${({ $narrow }) => ($narrow ? '44px' : 'auto')};
  vertical-align: top;
  font-size: 15px;
  color: ${INK};
`

const DocName = styled.div`
  font-weight: 700;
  overflow-wrap: anywhere;
`

const Sub = styled.div`
  margin-top: 2px;
  font-size: 14px;
  color: ${MUTED};
  overflow-wrap: anywhere;
`

const BpLink = styled(Link)`
  color: ${LINK};
  font-weight: 600;
`

const Warn = styled.div`
  margin-top: 6px;
  font-size: 14px;
  font-weight: 600;
  color: ${RAG.high.fg};
  overflow-wrap: anywhere;
`

const Pill = styled.span`
  display: inline-block;
  align-self: flex-start;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  cursor: help;
`

const RowActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const Cards = styled.div`
  display: none;
  flex-direction: column;
  gap: 10px;
  @media (max-width: 768px) { display: flex; }
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  background: #fff;
`

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`

const CardCheck = styled.span`
  flex: 0 0 auto;
  width: 28px;
  overflow: hidden;
`

const CardName = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  font-weight: 700;
  overflow-wrap: anywhere;
`

const DialogBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  font-size: 15px;
  color: ${INK};
`

const DialogTitle = styled.h2`
  margin: 0;
  font-size: 19px;
  font-weight: 700;
`

const Note = styled.textarea`
  min-height: 80px;
  padding: 10px 12px;
  border: 1px solid #d4d4d8;
  border-radius: 8px;
  font-size: 15px;
  font-family: inherit;
  resize: vertical;
`

const DialogFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`
